<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import CustomRoleForm from './CustomRoleForm.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const { t, te } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const showFormPopup = ref(false);
const showDeletePopup = ref(false);
const selectedRole = ref(null);

const records = computed(() => getters['customRoles/getCustomRoles'].value);
const uiFlags = computed(() => getters['customRoles/getUIFlags'].value);

const tableHeaders = computed(() => [
  t('CUSTOM_ROLE_MGMT.LIST.TABLE_HEADER.NAME'),
  t('CUSTOM_ROLE_MGMT.LIST.TABLE_HEADER.DESCRIPTION'),
  t('CUSTOM_ROLE_MGMT.LIST.TABLE_HEADER.PERMISSIONS'),
  t('CUSTOM_ROLE_MGMT.LIST.TABLE_HEADER.ACTION'),
]);

const permissionLabel = chave => {
  const i18nKey = `CUSTOM_ROLE_MGMT.PERMISSIONS.${chave.toUpperCase()}`;
  return te(i18nKey) ? t(i18nKey) : chave;
};

const openCreate = () => {
  selectedRole.value = null;
  showFormPopup.value = true;
};
const openEdit = role => {
  selectedRole.value = role;
  showFormPopup.value = true;
};
const hideForm = () => {
  showFormPopup.value = false;
};

const openDelete = role => {
  selectedRole.value = role;
  showDeletePopup.value = true;
};
const closeDelete = () => {
  showDeletePopup.value = false;
};

const confirmDeletion = async () => {
  const role = selectedRole.value;
  closeDelete();
  try {
    await store.dispatch('customRoles/delete', role.id);
    useAlert(t('CUSTOM_ROLE_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('CUSTOM_ROLE_MGMT.DELETE.API.ERROR_MESSAGE')
    );
  }
};

onBeforeMount(() => {
  store.dispatch('customRoles/get');
  store.dispatch('customRoles/getAvailablePermissions');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('CUSTOM_ROLE_MGMT.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('CUSTOM_ROLE_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('CUSTOM_ROLE_MGMT.HEADER')"
        :description="$t('CUSTOM_ROLE_MGMT.DESCRIPTION')"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('CUSTOM_ROLE_MGMT.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('CUSTOM_ROLE_MGMT.HEADER_BTN_TXT')"
            size="sm"
            @click="openCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="records"
        :no-data-message="$t('CUSTOM_ROLE_MGMT.LIST.404')"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="role in items" :key="role.id" :item="role">
            <template #default>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-12">
                  {{ role.name }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ role.description }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="permission in role.permissions"
                    :key="permission"
                    class="px-2 py-0.5 rounded text-body-main text-n-slate-12 bg-n-alpha-2"
                  >
                    {{ permissionLabel(permission) }}
                  </span>
                </div>
              </BaseTableCell>

              <BaseTableCell align="end">
                <div class="flex flex-shrink-0 gap-3 justify-end">
                  <Button
                    v-tooltip.top="$t('CUSTOM_ROLE_MGMT.FORM.EDIT')"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    @click="openEdit(role)"
                  />
                  <Button
                    v-tooltip.top="$t('CUSTOM_ROLE_MGMT.FORM.DELETE')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="uiFlags.isDeleting"
                    @click="openDelete(role)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal v-model:show="showFormPopup" :on-close="hideForm">
      <CustomRoleForm :custom-role="selectedRole" @close="hideForm" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('CUSTOM_ROLE_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('CUSTOM_ROLE_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="` ${selectedRole?.name}?`"
      :confirm-text="$t('CUSTOM_ROLE_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('CUSTOM_ROLE_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
